import { enqueueModalAtom } from '@/atoms/modalQueueAtoms';
import { getReverseTrialCohort } from '@/features/trialsAndUpgrades/utils/getReverseTrialCohort';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackNinetyDayTrialEndDiscreetClick,
  trackNinetyDayTrialEndDiscreetViewed,
} from '@/models/analytics/features/trackNinetyDayTrialAnalytics';
import { ModalPriority } from '@/types/modal-queue.types';
import { trialEndDiscreetCardMessages } from '@heidi/commerce';
import {
  NINETY_DAY_TRIAL_END_DISCREET_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
} from '@heidi/commerce/contracts';
import { useReverseTrial, useUserTier } from '@heidi/commerce/data';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

/**
 * Hook for {@link TrialEndDiscreetCard}: subscribes to the queue, fires the
 * "modal viewed" event on open, exposes button-click handlers that track
 * analytics + close the queue entry, and (for "Learn more") enqueues the big
 * post–trial-end modal as a user-triggered modal so it shows next.
 *
 * Pre-trial-end {@link import('../trial-end-reminder-popup/trial-end-reminder-popup').TrialEndReminderPopup}
 * is the structural template — same queue + analytics shape, different copy
 * and handoff target.
 */
export function useTrialEndDiscreetCard() {
  const intl = useIntl();
  const { isOpen, handleClose } = useQueuedModal(
    ModalId.NINETY_DAY_TRIAL_END_DISCREET_POPUP
  );
  const { userTier } = useUserTier();
  const { previousStatusBeforeClinicianTrial } = useReverseTrial();
  const enqueueModal = useSetAtom(enqueueModalAtom);

  const wasClinicianPlan =
    getReverseTrialCohort({
      previousStatusBeforeClinicianTrial,
      userTier,
    }) === 'clinician';
  const planName = intl.formatMessage(
    wasClinicianPlan
      ? trialEndDiscreetCardMessages.scribePlusPlanName
      : trialEndDiscreetCardMessages.scribeTeamPlanName
  );

  useEffect(() => {
    if (!isOpen) return;
    trackNinetyDayTrialEndDiscreetViewed({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndDiscreet,
    });
  }, [isOpen]);

  const handleLearnMore = useCallback(() => {
    trackNinetyDayTrialEndDiscreetClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndDiscreet,
      action: NINETY_DAY_TRIAL_END_DISCREET_ACTION.learnMore,
      label: intl.formatMessage(trialEndDiscreetCardMessages.learnMoreCta),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    handleClose();
    // Hand off to the big modal as a user-triggered enqueue. We don't
    // auto-queue the big modal at launch for low-usage admins, so this
    // is the only path that surfaces it for them.
    enqueueModal({
      id: ModalId.NINETY_DAY_TRIAL_END_MODAL,
      priority: ModalPriority.NORMAL,
      dismissible: false,
    });
  }, [enqueueModal, handleClose, intl, userTier]);

  const handleCloseClick = useCallback(() => {
    trackNinetyDayTrialEndDiscreetClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndDiscreet,
      action: NINETY_DAY_TRIAL_END_DISCREET_ACTION.close,
      label: intl.formatMessage(trialEndDiscreetCardMessages.close),
      ...(userTier !== undefined && { current_plan: userTier }),
    });
    handleClose();
  }, [handleClose, intl, userTier]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleClose();
      }
    },
    [handleClose]
  );

  return {
    isOpen,
    wasClinicianPlan,
    planName,
    handleLearnMore,
    handleCloseClick,
    handleOpenChange,
  };
}
